import { createContext, useState, useEffect } from "react";
import { getProfile, refreshTokens } from "../services/authenticationServices";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [accesstoken, setaccesstoken] = useState(localStorage.getItem("access_token"));
  const [refreshtoken, setrefreshtoken] = useState(localStorage.getItem("refresh_token"));
  const [loading, setLoading] = useState(true);
  const [profileData, setProfileData] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  const checkKeycloakAuth = () => {
    return !!localStorage.getItem("access_token");
  };

  const updateTokens = (token, refreshTokenVal) => {
    setaccesstoken(token);
    setrefreshtoken(refreshTokenVal);
    if (token) localStorage.setItem("access_token", token);
    if (refreshTokenVal) localStorage.setItem("refresh_token", refreshTokenVal);
  };

  const refreshToken = async () => {
    try {
      const res = await refreshTokens();
      const newToken = res?.data?.access_token;
      const newRefreshToken = res?.data?.refresh_token;
      if (newToken) {
        updateTokens(newToken, newRefreshToken);
        return newToken;
      }
      throw new Error("No token returned");
    } catch (error) {
      console.error("Token refresh error:", error);
      await logout();
      throw error;
    }
  };

  const logout = async () => {
    try {
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("hasCompletedOnboarding");
      localStorage.removeItem("preparation_year");
      localStorage.removeItem("upsc_journey_stage");
      localStorage.removeItem("email");
      localStorage.removeItem("user_id");
      localStorage.removeItem("hastour");
      sessionStorage.clear();

      setIsAuthenticated(false);
      setaccesstoken(null);
      setrefreshtoken(null);
      setProfileData(null);

      window.location.href = window.location.origin + "/";
    } catch (error) {
      console.error("Logout error:", error);
      window.location.href = window.location.origin + "/";
    }
  };

  const fetchProfile = async () => {
    try {
      const response = await getProfile();
      setProfileData(response);
      localStorage.setItem("hasCompletedOnboarding", response.has_seen_onboarding);
      localStorage.setItem("preparation_year", response.preparation_year);
      localStorage.setItem("upsc_journey_stage", response.upsc_journey_stage);
      localStorage.setItem("email", response.email);
      localStorage.setItem("referral_code", response.referral_code);
      localStorage.setItem("user_id", response.id);
      localStorage.setItem("hastour", response.has_seen_tour);
      return response;
    } catch (error) {
      console.error("Error fetching profile:", error);
      setProfileData(null);
      throw error;
    }
  };

  const loginSuccess = async (tokens) => {
    if (tokens?.access_token) {
      updateTokens(tokens.access_token, tokens.refresh_token);
    }
    setIsAuthenticated(true);
    try {
      await fetchProfile();
    } catch (e) {
      console.warn("Could not fetch profile immediately on loginSuccess:", e);
    }
  };

  const initAuth = async () => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      setIsAuthenticated(false);
      setLoading(false);
      setIsInitialized(true);
      return;
    }

    try {
      await fetchProfile();
      setIsAuthenticated(true);
    } catch (err) {
      console.warn("Initial profile fetch failed, attempting token refresh...");
      try {
        const refreshRes = await refreshTokens();
        const newToken = refreshRes?.data?.access_token;
        const newRefreshToken = refreshRes?.data?.refresh_token;
        if (newToken) {
          updateTokens(newToken, newRefreshToken);
          await fetchProfile();
          setIsAuthenticated(true);
        } else {
          await logout();
        }
      } catch (refreshErr) {
        console.error("Token refresh failed during init:", refreshErr);
        await logout();
      }
    } finally {
      setLoading(false);
      setIsInitialized(true);
    }
  };

  useEffect(() => {
    initAuth();
  }, []);

  // Backward-compatibility stub for components still referencing keycloak
  const keycloakStub = {
    authenticated: isAuthenticated,
    token: accesstoken || localStorage.getItem("access_token"),
    refreshToken: refreshtoken || localStorage.getItem("refresh_token"),
    updateToken: async () => {
      try {
        const res = await refreshTokens();
        return !!res?.data?.access_token;
      } catch {
        return false;
      }
    },
    logout: async () => logout(),
    login: () => { window.location.href = "/login"; },
    isTokenExpired: () => false,
    didInitialize: isInitialized
  };
  window.keycloakInstance = keycloakStub;

  if (loading) {
    return (
      <div className="loader-container">
        <div className="loader"></div>
      </div>
    );
  }

  const authContextValue = {
    isAuthenticated,
    keycloak: keycloakStub,
    accesstoken,
    refreshtoken,
    profileData,
    isInitialized,
    setProfileData,
    fetchProfile,
    refreshToken,
    logout,
    loginSuccess,
    checkKeycloakAuth,
    loading
  };

  window.authContext = authContextValue;

  return (
    <AuthContext.Provider value={authContextValue}>
      {children}
    </AuthContext.Provider>
  );
};
